import React, { useState, useEffect, useRef, useMemo, useContext } from 'react'
import JSZip from 'jszip';
import { ModulesContext } from '../App';
import '../styles/Batch.css'
import useBatchStatusApi from '../Hooks/useBatchStatusApi.js';
import useBatchDataAccessApi from '../Hooks/useBatchDataAccessApi';
import useAuthToken from '../Hooks/useAuthToken';
import BatchInfo from '../Components/Combined/BatchInfo';
import * as uuid from 'uuid';
import Loader from '../Components/Simple/Loader';
import { ApiError } from '../Components/Combined';
import { BackButton, Button, ErrorMessage, Select } from '../Components/Simple';
import FilePickerButton from '../Components/Simple/FilePickerButton';
import useLocalBatchData from '../Hooks/useLocalBatchData';
import DownloadLink from '../Components/Simple/MrpDownloadLink';
import useBatchValidation from '../Hooks/useBatchValidation.js';
import { EIGHT_THIRTY_SEVEN_BATCH_FILES, LARGE_BATCH_FILES } from '../Constants/moduleNames.js';
import { TEXT_FILE, ZIP_FILE } from '../Constants/fileTypes.js';
import useBatchApi from '../Hooks/useBatchApi.js';
import { MB_30 } from '../Constants/chunkDefaults.js';
import { enableDataAccessApi, enable837Format } from '../Utils/features';
import useDataAccessUpload from '../Hooks/useDataAccessUpload.js';
import { BATCH_UPLOAD } from '../Constants/dataAccessFileCategories.js';
import { SomethingWentWrong } from '../Constants/batchApiErrorMsg.js';

const outputFieldConfig = {
    SequenceNumber: true,
    ClaimID: true,
    LineNum: true,
    MemberID: true,
    ProviderGroup: true,
    ProviderCounty: true,
    MedicareID: true,
    EncounterID: true,
    MillimanBilled: true,
    MillimanAllowed: true,
    MillimanCurrentAllowed: true,
    ServiceType: true,
    RVU_DRG_HCPCS: true,
    RVU_DRG_HCPCS_TYPE: true,
    RVUAdjustedUnits: true,
    RVUFeeSchedule: true,
    RVUWorkRVUs: true,
    RVUPracticeRVUs: true,
    RVUMalpracticeRVUs: true,
    RVUFinalRVUs: true,
    RVUImputeBasis: true,
    RVUFinalRVUStep: true,
    RVUAdjudicated: true,
    RVUInclude: true,
    MedicareAdjustedUnits: true,
    MedicareAPC_DRG_HCPCS: true,
    MedicareAPC_DRG_HCPCS_TYPE: true,
    MedicareStatusLookup: true,
    MedicareFeeSchedule: true,
    MedicareAllowedBaseNTNWD: true,
    MedicareAllowedBase: true,
    MedicareOutlier: true,
    MedicareDSH_UCP: true,
    MedicareCapIME: true,
    MedicareOpIME: true,
    MedicareNewTech: true,
    MedicarePassThru_DGME: true,
    MedicarePassThru_Organ: true,
    MedicarePassThru_AlloStemCell: true,
    MedicarePassThru_Other: true,
    MedicareOrganAcqCharge: true,
    MedicareAllowed: true,
    MedicareAdjudicationErrors: true,
    FinancialValidationErrors: true,
    MedicareAdjudicated: true,
    MedicareInclude: true
};

export default function BatchSubmit() {
    const [selectedFile, setSelectedFile] = useState(null);
    const [batchList, setBatchList] = useState([]);
    const [localUploading, setLocalUploading] = useState(false);
    // const [localPageChange, setLocalPageChange] = useState(false); Phase 2
    const accessToken = useAuthToken();
    const [progress, setProgress] = useState(0);
    const { uploadBatchApi } = useBatchDataAccessApi();
    // Phase 2
    // const [currentPage, setCurrentPage] = useState(0);
    // const [itemsPerPage, setItemsPerPage] = useState(10);

    const { loading, result } = useBatchStatusApi();
    
    let upload, uploading;
    
    if (enableDataAccessApi) {
        const dataAccessUpload = useDataAccessUpload();
        upload = dataAccessUpload.execute;
        uploading = dataAccessUpload.loading;
    } else {
        const dataAccessApi = useBatchDataAccessApi();
        upload = dataAccessApi.upload;
        uploading = dataAccessApi.uploading;
    }

    const localBatchData = useLocalBatchData();
    const [fileError, setFileError] = useState('');
    const [uploadError, setUploadError] = useState('');
    const loaded = useRef(false);
    const [batchApiUploading, setBatchAPIUploading] = useState(false);
    // Phase 2
    // const [resultByUserId, setResultByUserId] = useState([]);
    const { modules } = useContext(ModulesContext);
    const lowerCaseModules = modules.map(module => module.toLowerCase());
    const ONE_HUNDRED_MB = 104857600;
    const isLargeBatchFilesPresent = lowerCaseModules.includes(LARGE_BATCH_FILES.toLowerCase());

    const CHUNK_SIZE_CUT_OFF = MB_30 * 2;
    const validation = useBatchValidation(enable837Format);


    const { createBatch, markBatchAsReady } = useBatchApi();

    useEffect(() => {
        if (!accessToken.objectId) {
            return;
        }

        setBatchList(bl => {
            const localCopies = localBatchData.get(accessToken.objectId);
            if (bl.length === 0) {
                return localCopies;
            }

            const newList = [];

            for (const batch of bl) {
                const matchingBatch = localCopies.find(r => r.batchId === batch.batchId);
                if (matchingBatch && (matchingBatch.batchCanceled || matchingBatch.batchDeleted)) {
                    newList.push(matchingBatch);
                } else {
                    newList.push(batch);
                }
            }

            for (const localCopy of localCopies) {
                if (!bl.some(l => l.batchId === localCopy.batchId)) {
                    newList.push(newBatch);
                }
            }

            return newList;
        });
    }, [accessToken.objectId]);

    // Phase 2
    // const fetchBatchData = async (isPageChange) => {
    //     try {
    //         const userBatches = await getBatchByUserId(accessToken.objectId, currentPage * itemsPerPage, itemsPerPage);
    //         // TODO - I just wanted to re-use this fetchBatchData method, so right now this param comes in to set the local page change var to false, to do find better solution
    //         // so that the next/prev button gets disabled only after this api call is done
    //         if (isPageChange) {
    //             setLocalPageChange(false);
    //         }
    //         const localCopies = localBatchData.get(accessToken.objectId);

    //         const combinedBatches = [...userBatches, ...localCopies];

    //         // Removing duplicates based on requestId (batchId)
    //         const uniqueBatches = combinedBatches.reduce((acc, current) => {
    //             const ids = acc.map(item => item.batchId); // Get all batchIds in the accumulator
    //             if (!ids.includes(current.batchId)) { // Check if the current batchId is not already in the accumulator
    //                 acc.push(current); // Add the current batch to the accumulator
    //             }
    //             return acc;
    //         }, []);

    //         setBatchList(uniqueBatches);
    //     } catch (error) {
    //         console.error('Error fetching batch data:', error);
    //     }
    // };

    // let timeoutHandle;
    // const executeFetchByUserLoop = async () => {
    //     try {
    //         if (!accessToken.objectId) {
    //             return;
    //         }
    //         const userBatches = await getBatchByUserId(accessToken.objectId, currentPage * itemsPerPage, itemsPerPage);
    //         timeoutHandle = setTimeout(executeFetchByUserLoop, 20000);
    //         setResultByUserId(userBatches);
    //     } catch (e) {
    //         console.error(e);
    //         timeoutHandle = setTimeout(executeFetchByUserLoop, 40000);
    //     }
    // };
    // useEffect(() => {
    //     executeFetchByUserLoop();
    // }, [accessToken.objectId]);

    // useEffect(() => {
    //     if (!accessToken.objectId) {
    //         return;
    //     }
    //     fetchBatchData(true);

    // }, [currentPage, itemsPerPage]);

    // useEffect(() => {
    //     if (!accessToken.objectId) {
    //         return;
    //     }
    //     setLocalDownload(true);

    //     fetchBatchData(false);

    //     setLocalDownload(false);

    // }, [accessToken.objectId]);

    useEffect(() => {
        if (!result) {
            return;
        }

        setBatchList(bl => {
            const updatedList = bl.map(batch => {
                const matchingBatch = result.find(r => r.batchId === batch.batchId);
                if (!batch.batchCanceled && !batch.batchDeleted && matchingBatch) {
                    // Update the existing batch with the new data
                    return matchingBatch;
                }
                return batch;
            });

            const newBatches = result.filter(newBatch => !bl.some(b => b.batchId === newBatch.batchId));

            return [...updatedList, ...newBatches];
        });

        if (!loaded.current) {
            loaded.current = true;
        }
    }, [result]);

    // Phase 2
    // useEffect(() => {
    //     if (!resultByUserId) {
    //         return;
    //     }

    //     setBatchList(bl => {
    //         const updatedList = bl.map(batch => {
    //             const matchingBatch = resultByUserId.find(r => r.batchId === batch.batchId);
    //             if (!batch.batchCanceled && !batch.batchDeleted && matchingBatch) {
    //                 // Update the existing batch with the new data
    //                 return matchingBatch;
    //             }
    //             return batch;
    //         });

    //         const newBatches = resultByUserId.filter(newBatch => !bl.some(b => b.batchId === newBatch.batchId));

    //         return [...updatedList, ...newBatches];
    //     });

    //     if (!loaded.current) {
    //         loaded.current = true;
    //     }
    // }, [resultByUserId]);

    useEffect(() => {
        if (!loaded.current) {
            return;
        }

        const filterdList = batchList.filter(bl => bl.local);
        localBatchData.save(filterdList, accessToken.objectId);
    }, [batchList]);

    const processBatchUpload = async (file, chunks) => {
        const batchId = await createBatch(file, chunks, enable837Format);
        if (batchId == null) {
            setUploadError('Failed to upload file: Failed to create batch!');
            setLocalUploading(false);
            return false;
        }
        
        const successfulUpload = await uploadBatchApi(file, batchId, setProgress, setBatchAPIUploading, setUploadError, enable837Format);
        if (!successfulUpload) {
            setUploadError('Failed to upload file');
            setLocalUploading(false);
            return false;
        }
    
        const { success, errorText } = await markBatchAsReady(batchId);
        if (!success) {
            setUploadError(errorText ?? SomethingWentWrong);
            setLocalUploading(false);
            return false;
        }
    
        setBatchList(bl => [
            ...bl,
            {
                local: true,
                batchId,
                uploads: []
            }
        ]);
    
        setSelectedFile(null);
        setLocalUploading(false);
        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setUploadError('');
        setProgress(0);

        if (!selectedFile) {
            return;
        }

        setLocalUploading(true);
        validation.reset();

        if (isLargeBatchFilesPresent && (selectedFile.size > CHUNK_SIZE_CUT_OFF || selectedFile.type !== "text/plain")) {
            let chunks = 1;
            if (selectedFile.size > CHUNK_SIZE_CUT_OFF) {
                chunks = Math.ceil(selectedFile.size / MB_30);
            }
            const result = await processBatchUpload(selectedFile, chunks);
            if (!result) {
                return; // processBatchUpload handles error message.
            }
        } else if (enable837Format && selectedFile.type !== "text/plain") {
            // If needed, define chunks value for the 837 branch. Here we use a default value.
            const chunks = 1;
            const result = await processBatchUpload(selectedFile, chunks);
            if (!result) {
                return;
            }
        } else {
            const batchId = uuid.v4();
            const uploadId = uuid.v4();

            const reader = new FileReader();
            reader.onload = (event) => {
                const fileContents = event.target.result;
                const lines = fileContents.split(/\r?\n/).filter(line => line.trim() !== ''); // Split by new lines and filter out empty lines
                const numLines = lines.length;
                setFileError('');

                handleFileLoadViaBatchDataAccess(batchId, uploadId, numLines);
            };

            reader.readAsText(selectedFile);
        }
    };

    const handleFileLoadViaBatchDataAccess = async (batchId, uploadId, fileSize) => {
        const success = await uploadFileViaBatchDataAccess(batchId, uploadId, fileSize);
        if (success) {
            setBatchList(bl => [
                ...bl,
                {
                    local: true,
                    batchId,
                    uploads: []
                }
            ]);

            setSelectedFile(null);
        } else {
            setUploadError('Failed to upload file')
        }
    };

    const handleFileClear = () => {
        setSelectedFile(null);
        validation.reset();
        

        setFileError('');
        setUploadError('');
    };

    const handleFileChange = (event) => {
        setFileError('');
        setUploadError('');
        validation.reset();
    
        const file = event.target.files[0];
        const fileName = file.name;
        const fileType = fileName.split('.').pop().toLowerCase();
    
        // Check file size only if 'largebatchfiles' is not present
        if (!isLargeBatchFilesPresent && file.size > ONE_HUNDRED_MB) {
            setFileError('File too large, please upload a file 100MB or less.');
        } else {
            if (enable837Format) {
                // All file types are allowed when the 837 flag is true
                // Always validate on txt file - can be either 837 batch or standard, validator skips validation if not tab delimeted
                if (fileType === TEXT_FILE) {
                    validation.validate(file);
                }
            } else if (isLargeBatchFilesPresent) {
                // For large batch files allow only .txt and .zip files
                if (fileType !== TEXT_FILE && fileType !== 'zip') {
                    setFileError("File must be a .txt or .zip file.");
                }
                // No validation performed
            } else {
                // When neither flag is true, only allow .txt files and run validation
                if (fileType === TEXT_FILE) {
                    validation.validate(file);
                } else {
                    setFileError("File not a .txt, please try a new file.");
                }
            }
        }
        setSelectedFile(file);
    };
    
    

    const handleOnCancelled = (batchId) => {
        setBatchList(bl => {
            const matchingBatch = bl.find(b => b.batchId === batchId);
            if (!matchingBatch) {
                return bl;
            }

            matchingBatch.local = true;
            matchingBatch.batchCanceled = true;

            return [
                ...bl.map(b => {
                    return {
                        ...b
                    }
                })
            ];
        });
    };

    const handleOnDeleted = (batchId) => {
        setBatchList(bl => {
            const matchingBatch = bl.find(b => b.batchId === batchId);
            if (!matchingBatch) {
                return bl;
            }

            matchingBatch.local = true;
            matchingBatch.batchDeleted = true;

            return [
                ...bl.map(b => {
                    return {
                        ...b
                    }
                })
            ];
        });
    };

    const uploadFileViaBatchDataAccess = async (batchId, uploadId, recordCount) => {
        // just pass in the user Id and the backend will figure out the clientId
        // ideally we wouldn't pass this into the backend either
        // the backend should be generating the zip file and metadata json
        const fileName = `${uploadId}_1.txt`
        const zip = new JSZip();
        zip.file(fileName, selectedFile);
        zip.file('metadata.json', JSON.stringify({
            userId: accessToken.objectId,
            batchId,
            uploadId,
            batchComplete: true,
            files: [
                {
                    fileName,
                    recordCount
                }
            ],
            outputFieldConfig
        }, null, 2));
        // Added for backwards compatability with the normal batch uploads with the progress bar
        // The progress bar is for chunking uploads progress, so we can either just not show it for normal uploads or keep it like this
        // These small batches upload so fast anyway it may not be needed, but I added it here for standardization
        setProgress(20);
        const content = await zip.generateAsync({ type: 'arrayBuffer' });
        let success;
        if (enableDataAccessApi) {
            success = await upload(BATCH_UPLOAD, `${batchId}.zip`, content);
        } else {
            success = await upload(content, `${batchId}.zip`);
        }
        setProgress(100);
        setTimeout(() => {
            setLocalUploading(false);
        }, 500); // 500 milliseconds delay so that the 100% progress can be seen
        return success;
    };


    const loadingFile = useMemo(() => {
        return validation.status === 'Loading';
    }, [validation]);

    const validating = useMemo(() => {
        return validation.status === 'InProgress';
    }, [validation]);

    const validationFailed = useMemo(() => {
        return validation.status === 'Failed';
    }, [validation]);

    const validationSuccess = useMemo(() => {
        return validation.status === 'Success';
    }, [validation]);

    const validationMessages = useMemo(() => {
        return validation?.messages;
    }, [validation]);

    const validationProgress = useMemo(() => {
        if (validation.status === 'InProgress') {
            return validation.progress?.toFixed(2);
        }

        return '0';
    }, [validation]);

    const loadingProgress = useMemo(() => {
        if (validation.status === 'Loading') {
            return validation.progress?.toFixed(2);
        }

        return '0';
    }, [validation]);

    // Handle page change
    // const handlePageChange = debounce((newPage) => {
    //     setLocalPageChange(true);
    //     setCurrentPage(newPage);
    // }, 100);

    // // Handle items per page change
    // const handleItemsPerPageChange = debounce((event) => {
    //     setLocalPageChange(true);
    //     setItemsPerPage(Number(event));
    //     setCurrentPage(0);
    // }, 100);

    return (
        <div className="row py-2">
            <div className="col">
                <div className="row justify-content-between my-2">
                    <div className="col-auto">
                        <h3>Batch Medicare Pricing Dashboard</h3>
                    </div>
                    <div className="col-auto">
                        <BackButton />
                    </div>
                    <div className="p-2 text-secondary">
                        Important: Initial batch access must be configured. Please contact MedicarePricer@Milliman.com for access.
                    </div>
                </div>

                <DownloadLink />
                <ErrorMessage errors={[uploadError]} />
                <hr />

                {localUploading && isLargeBatchFilesPresent && (
                    <div className="row mb-3">
                        <div className="col">
                            <div className="progress" style={{ width: '100%' }}>
                                <div
                                    className="progress-bar progress-bar-striped progress-bar-animated"
                                    role="progressbar"
                                    style={{ width: `${progress}%` }}
                                    aria-valuenow={progress}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                >
                                    {progress.toFixed(2)}%
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="row justify-content-between align-items-center mt-3">
                    <div className="col">
                        <ErrorMessage errors={[fileError]} />
                        <form onSubmit={handleSubmit}>
                            <div className="row align-items-center">
                                <div className="col-auto">
                                    <div className="row">
                                        <div className="col">
                                            <FilePickerButton id="batch-upload"
                                                text="Select Batch Data File for Pricing"
                                                className="btn-dark"
                                                disabled={uploading}
                                                onChange={handleFileChange} />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-4">
                                    {selectedFile?.name || 'No file selected'}
                                </div>
                                <div className="col-auto">
                                    <Button text="Upload"
                                        id="upload_button"
                                        type="submit"
                                        className="btn-primary"
                                        disabled={!selectedFile || localUploading || uploading || fileError || validating || validationFailed || loadingFile}
                                        loading={localUploading || uploading} />
                                </div>
                                <div className="col-auto">
                                    <Button text="Clear"
                                        id="clear-upload-button"
                                        className="btn-secondary"
                                        disabled={!selectedFile || localUploading || uploading}
                                        onClick={handleFileClear} />
                                </div>
                            </div>
                            <div className="row mt-3">
                                {validating && <div className="col-auto">
                                    Validating <Loader />
                                </div>}
                                {validating && validationProgress && <div className="col-auto">
                                    {validationProgress}%
                                </div>}
                                {loadingFile && <div className="col-auto">
                                    Preparing Validation <Loader />
                                </div>}
                                {loadingFile && loadingProgress && <div className="col-auto">
                                    {loadingProgress}%
                                </div>}
                                {validationSuccess && <div className="col-auto">
                                    <span className="text-success">Validated</span>
                                </div>}
                                {validationFailed && <div className="col-auto">
                                    <div className="text-danger">Validation Failed - {validationMessages.length} failure(s)</div>
                                    <div className="scrollable-container-300 p-3">
                                        <ol className="text-danger">
                                            {validationMessages.map((m, i) => {
                                                return <li key={i}>{m}</li>
                                            })}
                                        </ol>
                                    </div>
                                </div>}
                            </div>
                            <p id="upload_status"></p>
                            <p id="elig_upload"></p>
                        </form>
                    </div>
                    <div className="col-auto">
                        {loading && <Loader />}
                    </div>
                </div>
                <hr />
                Active Batch Processing
                <div className="batch-list">
                    {batchList.map((b, index) => {
                        return (<BatchInfo
                            key={index}
                            batch={b}
                            onCancel={handleOnCancelled}
                            onDelete={handleOnDeleted}
                        />)
                    })}
                </div>
                {/* Phase 2 */}
                {/* {isLargeBatchFilesPresent && <div className="pagination-controls">
                    <div className="row">
                        <div className="col-md-2">
                            <Select
                                id="batch-itemsPerPage"
                                label="Batches Per Page"
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                            </Select>
                        </div>
                        <div className="col-md-3 pt-4">
                            <Button
                                text="Previous"
                                id="prev-page-button"
                                className="btn-secondary"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 0 || localPageChange}
                            />

                            <span className='p-2'>Page {currentPage + 1}</span>

                            <Button
                                text="Next"
                                id="next-page-button"
                                className="btn-primary"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={batchList.length < itemsPerPage || localPageChange}
                            />
                        </div>
                    </div>
                </div>} */}
            </div>
        </div>
    )
};